import { css } from 'aphrodite';
import * as React from 'react';
import { styles } from './styles';

const TipCard = ({ title, children }: React.PropsWithChildren<{ title: string }>) => (
	<section className={css(styles.tipCard)}>
		<h3 className={css(styles.tipTitle)}>{title}</h3>
		{children}
	</section>
);

export const HowItWorksTip = () => (
	<TipCard title='How Coach Lab works'>
		<ul className={css(styles.tipList)}>
			<li>You play the SDR. An AI prospect built from a real lead answers the phone.</li>
			<li>Every turn, the coach you are testing shows its card, the same way it would for a rep on a live call.</li>
			<li>You say what you would really say: the card's line, your own version, or something else entirely.</li>
			<li>Your notes on the cards become changes to your coach. Then you call again on the updated version.</li>
		</ul>
	</TipCard>
);

export const FeedbackTip = () => (
	<TipCard title='What makes useful feedback'>
		<ul className={css(styles.tipList)}>
			<li>
				<strong>Note the card, not the prospect.</strong> "The card pitched before she said what she cares about" is
				something we can change. "She was tough" usually is not.
			</li>
			<li>
				<strong>Say what it should have done instead,</strong> ideally with the words you would have used.
			</li>
			<li>
				<strong>Play it like a real call.</strong> Where you go off-card is some of the most useful information.
			</li>
			<li>
				<strong>Several calls beat one.</strong> Three or four calls on different prospects show a pattern that one call
				cannot.
			</li>
		</ul>
	</TipCard>
);

export const HardOnPurposeTip = () => (
	<TipCard title='The prospects are hard on purpose'>
		<p className={css(styles.help)}>
			Each prospect goes as far as real prospects go, and only one step further if you do something clearly better than
			a typical call: ask about them before asking for anything, listen, and handle pushback without pushing. When you
			ask for the next step, Coach Lab checks whether you earned it and tells you why under your line. A prospect set to
			say "not interested" will not book however good your line is. A booking rate far above what our reps get would
			mean the prospects are too easy, not that the coach is good.
		</p>
		<p className={css(styles.help)}>
			To spend your time on the pitch and the objections, tick <strong>Longer calls</strong>. After each call, the
			prospect says why it ended the call, so you can tell where it was set to stop from what your lines caused.
		</p>
	</TipCard>
);

export const AfterTheCallTip = () => (
	<TipCard title='What happens to your sessions'>
		<ul className={css(styles.tipList)}>
			<li>
				Save each call as you finish it. When you are done for the sitting, download them all in one file and send it to
				Jes.
			</li>
			<li>
				Your notes become changes to your sandbox, usually the same day. The facts about Levitate, the card format and
				the honesty, names and times rules stay locked.
			</li>
			<li>When your version is ready, it goes into an A/B test with a group of reps, and real results decide.</li>
		</ul>
	</TipCard>
);

export const DuringTheCallTip = () => (
	<TipCard title='During the call'>
		<ul className={css(styles.tipList)}>
			<li>
				<strong>Say the card's line</strong> sends the card's quoted line, word for word.
			</li>
			<li>
				<strong>Good</strong> and <strong>Bad</strong> are a thumbs up or down on the card, with a reason.
			</li>
			<li>
				<strong>Note</strong> is anything else about the card, for example "should have asked for the time here".
			</li>
			<li>
				<strong>Prospect isn't realistic</strong> is for when the AI prospect is not acting like a real owner would.
			</li>
			<li>Notes go to us, never to the prospect. Add as many as you like, then say your line.</li>
		</ul>
	</TipCard>
);

export const RealCallTip = () => (
	<TipCard title='Reviewing a real call'>
		<ul className={css(styles.tipList)}>
			<li>These are the cards the rep saw, from the live coach, with what was said around each one.</li>
			<li>
				<strong>Good</strong>, <strong>Bad</strong> and <strong>Note</strong> work as in a role-play. Use the box under
				a card for what it should have told the rep to say.
			</li>
			<li>
				<strong>What would my coach say here?</strong> asks your LAB coach for its card at that moment of the same call,
				with the same Game Plan. If it already does better, your changes are working. If not, your note says what to
				fix.
			</li>
			<li>Save the review. It joins your role-plays in Calls this sitting, and downloads with them.</li>
		</ul>
	</TipCard>
);
